import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Building2, LogOut, ChevronDown } from 'lucide-react';
import { useAuth } from '../lib/auth';
import NotificationBell from './NotificationBell';

export interface NavItem {
  to: string;
  label: string;
  icon: React.ReactNode;
  end?: boolean;
}

interface PortalLayoutProps {
  title: string;
  portalColor?: string;
  navItems: NavItem[];
  children: React.ReactNode;
}

export default function PortalLayout({ title, portalColor = 'text-primary', navItems, children }: PortalLayoutProps) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [profileOpen, setProfileOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate(rolePath);
  };

  const rolePath = user?.role === 'account' ? '/accounts' : user?.role === 'oversight' ? '/it' : `/${user?.role}`;

  const activePath = (to: string, end?: boolean) => {
    const path = window.location.pathname;
    if (end) return path === to;
    return path === to || path.startsWith(`${to}/`) || (to === '/admin' && path === '/admin');
  };

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="flex">
        {/* Sidebar */}
        <aside className={`${mobileOpen ? 'translate-x-0' : '-translate-x-full'} fixed inset-y-0 left-0 z-40 w-64 bg-obsidian border-r border-primary/30 shadow-lg transition-transform duration-200 lg:translate-x-0 flex flex-col`}>
          <div className="flex-1 overflow-y-auto p-6">
            <Link to="/" className="flex items-center space-x-2 mb-8">
              <Building2 className={`h-8 w-8 ${portalColor}`} />
              <div>
                <span className="text-xl font-bold block leading-tight text-white">{title}</span>
                <span className="text-xs text-taupe">Naya Edwin Solutions</span>
              </div>
            </Link>
            <nav className="space-y-1 pb-4">
              {navItems.map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileOpen(false)}
                  className={`flex items-center space-x-3 p-3 rounded-lg text-sm font-medium transition-colors ${
                    activePath(item.to, item.end)
                      ? 'bg-primary text-black'
                      : 'text-white/70 hover:bg-primary/10 hover:text-primary'
                  }`}
                >
                  {item.icon}
                  <span>{item.label}</span>
                </Link>
              ))}
            </nav>
          </div>
          <div className="shrink-0 p-6 border-t border-primary/20">
            <Link to="/" className="flex items-center text-sm text-taupe hover:text-primary">
              <Building2 className="h-4 w-4 mr-2" /> View Public Site
            </Link>
          </div>
        </aside>

        {mobileOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
        )}

        {/* Main */}
        <div className="flex-1 lg:ml-64 flex flex-col min-h-screen">
          {/* Top bar */}
          <header className="bg-white shadow-sm px-4 sm:px-8 py-3 flex items-center justify-between sticky top-0 z-20">
            <button
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 text-gray-600"
              onClick={() => setMobileOpen(true)}
              aria-label="Open menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            <div className="flex-1" />

            <div className="flex items-center space-x-3">
              <NotificationBell />
              <div className="relative">
                <button
                  onClick={() => setProfileOpen(!profileOpen)}
                  className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-100"
                >
                  {user?.image_url ? (
                    <img src={user.image_url} alt={user?.name} className="h-8 w-8 rounded-full object-cover" />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-primary text-black flex items-center justify-center text-xs font-semibold">
                      {userInitials || 'U'}
                    </div>
                  )}
                  <div className="hidden sm:block text-left">
                    <p className="text-sm font-medium leading-tight">{user?.name}</p>
                    <p className="text-xs text-gray-400 capitalize">{user?.role}</p>
                  </div>
                  <ChevronDown className="h-4 w-4 text-gray-400 hidden sm:block" />
                </button>
                {profileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50">
                    <Link
                      to={`${rolePath}/profile`}
                      onClick={() => setProfileOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      My Profile
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
                    >
                      <LogOut className="h-4 w-4 mr-2" /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            </div>
          </header>

          {/* Content */}
          <main className="flex-1 p-4 sm:p-8">{children}</main>
        </div>
      </div>
    </div>
  );
}
